import React, { useState, useEffect, useMemo } from 'react';
import { PlayCircle } from 'lucide-react';
import { validateImageUrl } from '@/lib/utils';
import AdComponent from '@/components/ads/AdComponent';
import { getVideoThumbnail } from '@/lib/utils/videoUtils';
import { ImageLightbox } from './ImageLightbox';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';

interface MediaItem {
  type: 'image' | 'video';
  url: string;
  thumbnailUrl?: string;
}

interface ProductMediaGalleryProps {
  images?: string[];
  videoUrl?: string;
  title: string;
  isNewUpload?: boolean;
}

export const ProductMediaGallery: React.FC<ProductMediaGalleryProps> = ({ 
  images,
  videoUrl,
  title,
  isNewUpload = false 
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [mediaErrors, setMediaErrors] = useState<boolean[]>([]);
  const [processedMediaItems, setProcessedMediaItems] = useState<MediaItem[]>([]);
  const [loadingMedia, setLoadingMedia] = useState<boolean[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  
  const placeholderImage = 'https://placeholder.pics/svg/300x240/DEDEDE/555555?text=No+Media';
  const videoPlaceholderThumbnail = 'https://placeholder.pics/svg/300x240/DEDEDE/555555/Video?label=Video';

  // Get only image URLs for lightbox
  const imageOnlyUrls = useMemo(() => {
    return processedMediaItems
      .filter(item => item.type === 'image')
      .map(item => item.url);
  }, [processedMediaItems]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = () => {
      setCurrentMediaIndex(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    onSelect();

    return () => {
      api?.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    const processMedia = async () => {
      const newMediaItems: MediaItem[] = [];

      if (images && images.length > 0) {
        images.filter(url => url && url.trim() !== '').forEach(url => {
          const cleanUrl = validateImageUrl(url, placeholderImage, isNewUpload);
          newMediaItems.push({ type: 'image', url: cleanUrl, thumbnailUrl: cleanUrl });
        });
      }

      if (videoUrl && videoUrl.trim() !== '') {
        const thumbnail = await getVideoThumbnail(videoUrl, videoPlaceholderThumbnail);
        newMediaItems.push({ type: 'video', url: videoUrl, thumbnailUrl: thumbnail });
      }

      if (newMediaItems.length === 0) {
        setProcessedMediaItems([{ type: 'image', url: placeholderImage, thumbnailUrl: placeholderImage }]);
        setMediaErrors([false]);
        setLoadingMedia([false]);
        if (currentMediaIndex !== 0) setCurrentMediaIndex(0);
        return;
      }
      
      setProcessedMediaItems(newMediaItems);
      setMediaErrors(new Array(newMediaItems.length).fill(false));
      setLoadingMedia(new Array(newMediaItems.length).fill(true));
      
      if (currentMediaIndex >= newMediaItems.length) {
        setCurrentMediaIndex(0);
      }
    };

    processMedia();
  }, [images, videoUrl, isNewUpload, placeholderImage, videoPlaceholderThumbnail]);

  const handleMediaError = (index: number) => {
    setMediaErrors(prev => {
      const newErrors = [...prev];
      newErrors[index] = true;
      return newErrors;
    });
    setLoadingMedia(prev => {
      const newLoading = [...prev];
      newLoading[index] = false;
      return newLoading;
    });
  };

  const handleMediaLoad = (index: number) => {
    setLoadingMedia(prev => {
      const newLoading = [...prev];
      newLoading[index] = false;
      return newLoading;
    });
  };

  const handleImageClick = () => {
    const currentMedia = processedMediaItems[currentMediaIndex];
    if (currentMedia && currentMedia.type === 'image') {
      // Find the index of current image in the images-only array
      const imageIndex = imageOnlyUrls.findIndex(url => url === currentMedia.url);
      if (imageIndex !== -1) {
        setLightboxImageIndex(imageIndex);
        setLightboxOpen(true);
      }
    }
  };

  const handleThumbnailClick = (index: number) => {
    const clickedMedia = processedMediaItems[index];
    if (clickedMedia.type === 'image') {
      // Find the index in the images-only array
      const imageIndex = imageOnlyUrls.findIndex(url => url === clickedMedia.url);
      if (imageIndex !== -1) {
        setLightboxImageIndex(imageIndex);
        setLightboxOpen(true);
      }
    } else {
      // For videos, use the carousel API to navigate
      api?.scrollTo(index);
    }
  };
  
  const currentMedia = useMemo(() => {
    if (!processedMediaItems || processedMediaItems.length === 0 || !processedMediaItems[currentMediaIndex]) {
      return { type: 'image', url: placeholderImage, thumbnailUrl: placeholderImage } as MediaItem;
    }
    const media = processedMediaItems[currentMediaIndex];
    let fallbackThumb = media.type === 'video' ? videoPlaceholderThumbnail : placeholderImage;
    return {
      ...media,
      thumbnailUrl: media.thumbnailUrl || fallbackThumb
    };
  }, [processedMediaItems, currentMediaIndex, placeholderImage, videoPlaceholderThumbnail]);

  if (!processedMediaItems || processedMediaItems.length === 0) {
    return (
      <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-[4/3]">
        <div className="w-full h-full flex items-center justify-center text-gray-400">
          Keine Medien verfügbar
        </div>
      </div>
    );
  }
  
  const validMediaCount = processedMediaItems.length;
  const shouldShowAd = processedMediaItems.length > 2 && currentMediaIndex > 0 && currentMediaIndex % 2 === 0;

  return (
    <div>
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          align: "center",
          loop: true,
          dragFree: false,
        }}
      >
        <CarouselContent>
          {processedMediaItems.map((media, index) => (
            <CarouselItem key={index}>
              <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-[16/9] md:aspect-[4/3]">
                {loadingMedia[index] && media.type === 'image' && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <span className="text-gray-400">Medien werden geladen...</span>
                  </div>
                )}
                
                {media.type === 'image' ? (
                  <img 
                    src={mediaErrors[index] ? placeholderImage : media.url} 
                    alt={title}
                    className={`w-full h-full object-cover cursor-pointer ${loadingMedia[index] ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
                    onError={() => handleMediaError(index)}
                    onLoad={() => handleMediaLoad(index)}
                    onClick={handleImageClick}
                    loading="lazy"
                  />
                ) : (
                  <video
                    key={media.url}
                    controls
                    src={mediaErrors[index] ? '' : media.url}
                    className="w-full h-full object-contain bg-black"
                    onError={() => handleMediaError(index)}
                    onLoadedData={() => handleMediaLoad(index)}
                    onCanPlay={() => handleMediaLoad(index)}
                  >
                    Dein Browser unterstützt das Video-Tag nicht.
                  </video>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        {processedMediaItems.length > 1 && validMediaCount > 1 && (
          <>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
            
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
              {processedMediaItems.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full ${index === currentMediaIndex ? 'bg-white' : 'bg-white/50'}`}
                  onClick={() => api?.scrollTo(index)}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
      
      {processedMediaItems.length > 1 && (
        <div className="grid grid-cols-4 gap-2 mt-2">
          {processedMediaItems.map((item, index) => (
            <button
              key={index}
              className={`relative rounded-md overflow-hidden ${index === currentMediaIndex ? 'ring-2 ring-brand-500' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img 
                src={item.thumbnailUrl || (item.type === 'video' ? videoPlaceholderThumbnail : placeholderImage)} 
                alt={`Thumbnail ${index + 1} für ${item.type}`} 
                className={`w-full h-24 object-cover ${loadingMedia[index] && item.type === 'image' ? 'opacity-50' : 'opacity-100'}`}
                onError={(e) => { 
                  const target = e.target as HTMLImageElement;
                  target.src = item.type === 'video' ? videoPlaceholderThumbnail : placeholderImage;
                }}
                loading="lazy"
              />
              {item.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <PlayCircle className="w-8 h-8 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Ad Component moved to after thumbnail grid */}
      {shouldShowAd && (
        <div className="my-4">
          <AdComponent adSlot="1234567890" adFormat="rectangle" />
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={imageOnlyUrls}
        currentIndex={lightboxImageIndex}
        onNavigate={setLightboxImageIndex}
        title={title}
      />
    </div>
  );
};
